/**
 * NewCustomerButton — V3 D6.4 (2026-05-08)
 *
 * Small "🔄 عميل جديد" pill that lives in every step's header. On
 * click it opens a confirmation modal; on confirm it:
 *
 *   1. clears the persisted Zustand session (resetSession())
 *   2. drops the splash-seen + welcome-form + payment-plan flags
 *   3. full-page reload to the current section's root so any hung
 *      listeners / cached component state / sessionStorage residue
 *      starts from a true blank slate
 *
 * Section detection: pathname starts with /v2 → reload to /v2/, else
 * reload to /. This preserves the v1 reset flow unchanged while the
 * v2 surfaces (V2PageShell uses this button on every page) stay
 * inside v2 instead of getting bounced back to v1's RootGate.
 */

import { useEffect, useState } from 'react';
import { RotateCcw, AlertTriangle } from 'lucide-react';
import { useT } from '@lib/i18n';
import { useSessionStore } from '@stores/sessionStore';

interface NewCustomerButtonProps {
  className?: string;
}

export default function NewCustomerButton({ className = '' }: NewCustomerButtonProps) {
  const t = useT();
  const resetSession = useSessionStore((s) => s.resetSession);

  const [open, setOpen] = useState(false);

  // Esc closes the confirm modal — same pattern as the other modals.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const handleConfirm = () => {
    try {
      sessionStorage.removeItem('key2:hasSeenSplash');
      sessionStorage.removeItem('key2:v2.welcomeForm');
      sessionStorage.removeItem('key2:v2.paymentPlan');
    } catch {
      /* private mode — ignore */
    }
    resetSession();
    setOpen(false);
    // Full-page reload to the current section root. Auto-detection
    // keeps v1 going to / and v2 staying inside /v2/, so a reset
    // from /v2/finance no longer lands the user back in v1.
    const isV2 = window.location.pathname.startsWith('/v2');
    window.location.href = isV2 ? '/v2/' : '/';
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={t('common.newCustomer')}
        className={[
          'inline-flex items-center gap-1.5 h-9 px-3 rounded-full',
          'text-xs font-semibold',
          'bg-navy-50 text-navy-600 border border-navy-100',
          'hover:bg-gold hover:text-navy hover:border-gold',
          'transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60',
          className,
        ].join(' ')}
      >
        <RotateCcw className="w-3.5 h-3.5" />
        <span>{t('common.newCustomer')}</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('common.newCustomerConfirmTitle')}
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/55 backdrop-blur-sm p-4 animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="card max-w-sm w-full p-6 text-center shadow-xl">
            <div className="mx-auto w-12 h-12 rounded-full bg-gold/15 text-gold flex items-center justify-center mb-3">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h2 className="text-lg font-bold text-navy mb-2">
              {t('common.newCustomerConfirmTitle')}
            </h2>
            <p className="text-sm text-navy-600 mb-5">
              {t('common.newCustomerConfirmMessage')}
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-center">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="btn-ghost"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className="btn-gold"
                autoFocus
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
